import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Paper, Button, CircularProgress } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import PageContainer from "../../common_components/PageContainer";
import { useLayout } from "../../contexts/LayoutContext";
import { useAccountOverview } from "../../context/AccountOverviewContext";
import ConfirmActionModal from "../../common_components/ConfirmActionModal";
import Pagination from "../../common_components/Pagination";
import { FloatingSaveBar } from "../../common_components/FloatingSaveBar";
import { Trash2 } from "lucide-react";
import SyncLogHeader from "./Component/SyncLogHeader";
import ErrorLogList from "./Component/ErrorLogList";
import SyncLogList from "./Component/SyncLogList";
import { EditErrorLogDrawer, AddEditSyncLogDrawer } from "./Drawer/AddEditSyncLogDrawer";
import { fetchSyncLogs, fetchSyncLogTypes, updateSyncLog, createBackfillSyncLogs, deleteErrorLog, deleteSyncLog, selectErrorLogs, selectErrorLogPagination, selectErrorLogLoading, selectErrorLogTypes, selectSyncLogs, selectSyncLogPagination, selectSyncLogLoading, selectSyncLogTypes, selectSyncLogClients, selectSyncLogSaving, } from "../../redux/syncLogSlice";
const SYNC_LOG_PAGINATION = {
    DEFAULT_PAGE: 1,
    LIMIT: 50,
};
const SyncLogMain = () => {
    const dispatch = useDispatch();
    const { setIsSidebarExpanded } = useLayout();
    // Archive mode (Active/Archive toggle in the header) → Client filter lists archived (inactive) clients.
    const { isArchiveMode } = useAccountOverview();
    // Collapse the sidebar submenu on visit (same behavior as Email Configuration).
    useEffect(() => {
        setIsSidebarExpanded(false);
    }, [setIsSidebarExpanded]);
    const errorLogs = useSelector(selectErrorLogs);
    const errorLogPagination = useSelector(selectErrorLogPagination);
    const errorLogLoading = useSelector(selectErrorLogLoading);
    const syncLogs = useSelector(selectSyncLogs);
    const syncLogPagination = useSelector(selectSyncLogPagination);
    const syncLogLoading = useSelector(selectSyncLogLoading);
    const saving = useSelector(selectSyncLogSaving);
    const errorLogTypes = useSelector(selectErrorLogTypes);
    const syncLogTypes = useSelector(selectSyncLogTypes);
    const syncLogClients = useSelector(selectSyncLogClients);
    const [searchParams] = useSearchParams();
    const initialTab = searchParams.get("tab") || "sync_logs";
    const initialType = searchParams.get("type") || "";
    const [activeTab, setActiveTab] = useState(initialTab);
    const [searchText, setSearchText] = useState("");
    // Debounced search drives the fetch (350ms) so typing doesn't fire a request per keystroke.
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
        return () => clearTimeout(t);
    }, [searchText]);
    // Type is split per tab — each tab has its own vocabulary and remembers its own selection.
    const [headerStatus, setHeaderStatus] = useState(searchParams.get("status") || "");
    const [errorHeaderType, setErrorHeaderType] = useState(initialTab === "error_logs" ? initialType : "");
    const [syncHeaderType, setSyncHeaderType] = useState(initialTab === "sync_logs" ? initialType : "");
    const [headerNetwork, setHeaderNetwork] = useState("");
    const [headerClientId, setHeaderClientId] = useState([]);
    // The Type value shown in the header reflects the active tab's remembered selection.
    const activeHeaderType = activeTab === "error_logs" ? errorHeaderType : syncHeaderType;
    const setActiveHeaderType = useCallback((v) => {
        if (activeTab === "error_logs")
            setErrorHeaderType(v);
        else
            setSyncHeaderType(v);
    }, [activeTab]);
    const [errorLogPage, setErrorLogPage] = useState(SYNC_LOG_PAGINATION.DEFAULT_PAGE);
    const [syncLogPage, setSyncLogPage] = useState(SYNC_LOG_PAGINATION.DEFAULT_PAGE);
    const [sortConfig, setSortConfig] = useState({ columnId: null, direction: "asc" });
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [columnOrder, setColumnOrder] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({ left: [], right: [] });
    const [editErrorLogDrawerOpen, setEditErrorLogDrawerOpen] = useState(false);
    const [selectedErrorLog, setSelectedErrorLog] = useState(null);
    const [editSyncLogDrawerOpen, setEditSyncLogDrawerOpen] = useState(false);
    const [selectedSyncLog, setSelectedSyncLog] = useState(null);
    const [syncLogViewOnly, setSyncLogViewOnly] = useState(false);
    const [deleteState, setDeleteState] = useState({ open: false, loading: false, type: null, ids: [] });
    const [selectedErrorLogIds, setSelectedErrorLogIds] = useState(new Set());
    const [selectedSyncLogIds, setSelectedSyncLogIds] = useState(new Set());
    const isFirstLoad = useRef(true);
    // Filter options (types + clients) once on mount — light query, reliable; log fetches run in the effect below.
    useEffect(() => {
        dispatch(fetchSyncLogTypes({ status: isArchiveMode ? 'inactive' : 'active' }));
        // Switching Active/Archive swaps the client set, so clear any selected client filter.
        setHeaderClientId([]);
    }, [dispatch, isArchiveMode]);
    // Single combined fetch — one round trip fills both tabs; each keeps its own page + type, so tab switches don't refetch.
    useEffect(() => {
        dispatch(fetchSyncLogs({
            error_page: errorLogPage,
            error_limit: SYNC_LOG_PAGINATION.LIMIT,
            error_request_type: errorHeaderType || undefined,
            sync_page: syncLogPage,
            sync_limit: SYNC_LOG_PAGINATION.LIMIT,
            sync_type: syncHeaderType || undefined,
            search: debouncedSearch || undefined,
            sort_by: sortConfig.columnId || undefined,
            sort_order: sortConfig.columnId ? sortConfig.direction : undefined,
            client_id: headerClientId.length ? headerClientId.join(",") : undefined,
            network: headerNetwork || undefined,
            status: headerStatus || undefined,
        }));
    }, [
        dispatch, errorLogPage, syncLogPage, debouncedSearch,
        sortConfig.columnId, sortConfig.direction,
        headerClientId, headerNetwork, headerStatus,
        errorHeaderType, syncHeaderType,
    ]);
    // Mark first load done once we have data
    useEffect(() => {
        if ((errorLogs.length > 0 || syncLogs.length > 0) && isFirstLoad.current) {
            isFirstLoad.current = false;
        }
    }, [errorLogs, syncLogs]);
    // Re-fetch the current view (one call refreshes both the scheduler and error log tabs).
    const handleRefresh = useCallback(() => {
        dispatch(fetchSyncLogs({
            error_page: errorLogPage,
            error_limit: SYNC_LOG_PAGINATION.LIMIT,
            error_request_type: errorHeaderType || undefined,
            sync_page: syncLogPage,
            sync_limit: SYNC_LOG_PAGINATION.LIMIT,
            sync_type: syncHeaderType || undefined,
            search: debouncedSearch || undefined,
            sort_by: sortConfig.columnId || undefined,
            sort_order: sortConfig.columnId ? sortConfig.direction : undefined,
            client_id: headerClientId.length ? headerClientId.join(",") : undefined,
            network: headerNetwork || undefined,
            status: headerStatus || undefined,
        }));
    }, [
        dispatch, errorLogPage, syncLogPage, debouncedSearch,
        sortConfig.columnId, sortConfig.direction,
        headerClientId, headerNetwork, headerStatus,
        errorHeaderType, syncHeaderType,
    ]);
    // Reset both pages when shared filters change (activeTab excluded so tab switches keep the page).
    useEffect(() => {
        setErrorLogPage(SYNC_LOG_PAGINATION.DEFAULT_PAGE);
        setSyncLogPage(SYNC_LOG_PAGINATION.DEFAULT_PAGE);
    }, [
        debouncedSearch, sortConfig.columnId, sortConfig.direction,
        headerStatus, headerNetwork, headerClientId,
    ]);
    // Each tab's type filter only resets its own page.
    useEffect(() => { setErrorLogPage(SYNC_LOG_PAGINATION.DEFAULT_PAGE); }, [errorHeaderType]);
    useEffect(() => { setSyncLogPage(SYNC_LOG_PAGINATION.DEFAULT_PAGE); }, [syncHeaderType]);
    const handleSortChange = useCallback((columnId, direction) => setSortConfig({ columnId, direction }), []);
    const handleEditErrorLog = useCallback((log) => {
        setSelectedErrorLog(log);
        setEditErrorLogDrawerOpen(true);
    }, []);
    // Clear selection when filters/search/tab change (rows about to change)
    useEffect(() => {
        setSelectedErrorLogIds(new Set());
        setSelectedSyncLogIds(new Set());
    }, [
        activeTab, searchText, headerStatus, headerNetwork, headerClientId,
        errorHeaderType, syncHeaderType, errorLogPage, syncLogPage,
    ]);
    const handleBulkDeleteErrorLogs = useCallback(() => {
        if (selectedErrorLogIds.size === 0)
            return;
        setDeleteState({ open: true, loading: false, type: "error", ids: Array.from(selectedErrorLogIds) });
    }, [selectedErrorLogIds]);
    const handleBulkDeleteSyncLogs = useCallback(() => {
        if (selectedSyncLogIds.size === 0)
            return;
        setDeleteState({ open: true, loading: false, type: "sync", ids: Array.from(selectedSyncLogIds) });
    }, [selectedSyncLogIds]);
    const handleErrorLogDrawerClose = useCallback(() => {
        setEditErrorLogDrawerOpen(false);
        setSelectedErrorLog(null);
    }, []);
    const handleEditSyncLog = useCallback((log) => {
        setSelectedSyncLog(log);
        setSyncLogViewOnly(false);
        setEditSyncLogDrawerOpen(true);
    }, []);
    const handleViewSyncLog = useCallback((log) => {
        setSelectedSyncLog(log);
        setSyncLogViewOnly(true);
        setEditSyncLogDrawerOpen(true);
    }, []);
    const handleSyncLogDrawerClose = useCallback(() => {
        setEditSyncLogDrawerOpen(false);
        setSelectedSyncLog(null);
        setSyncLogViewOnly(false);
    }, []);
    const handleSaveSyncLog = useCallback(async (data) => {
        try {
            await dispatch(updateSyncLog({
                id: data._id,
                priority: data.priority ? Number(data.priority) : undefined,
                status: data.status,
                error: data.error,
            })).unwrap();
            setEditSyncLogDrawerOpen(false);
            setSelectedSyncLog(null);
        }
        catch (error) {
            console.error("Failed to save sync log:", error);
        }
    }, [dispatch]);
    const [createSyncLogDrawerOpen, setCreateSyncLogDrawerOpen] = useState(false);
    const handleOpenCreateSyncLog = useCallback(() => {
        setCreateSyncLogDrawerOpen(true);
    }, []);
    const handleCreateSyncLogClose = useCallback(() => {
        setCreateSyncLogDrawerOpen(false);
    }, []);
    const handleCreateSyncLog = useCallback(async (data) => {
        try {
            // One request fans the selected clients/networks/connections + date range
            // into per-network chunked sync logs server-side. Empty arrays mean "All".
            await dispatch(createBackfillSyncLogs({
                client_ids: data.client_ids,
                networks: data.networks,
                connection_ids: data.connection_ids,
                type: data.type || undefined,
                startDate: data.start_date,
                endDate: data.end_date,
                priority: data.priority ? Number(data.priority) : undefined,
                status: data.status || undefined,
            })).unwrap();
            setCreateSyncLogDrawerOpen(false);
            // Created rows arrive via refetch (the response is a summary, not full rows).
            setSyncLogPage(SYNC_LOG_PAGINATION.DEFAULT_PAGE);
            dispatch(fetchSyncLogs({
                error_page: errorLogPage,
                error_limit: SYNC_LOG_PAGINATION.LIMIT,
                error_request_type: errorHeaderType || undefined,
                sync_page: SYNC_LOG_PAGINATION.DEFAULT_PAGE,
                sync_limit: SYNC_LOG_PAGINATION.LIMIT,
                sync_type: syncHeaderType || undefined,
                search: debouncedSearch || undefined,
                sort_by: sortConfig.columnId || undefined,
                sort_order: sortConfig.columnId ? sortConfig.direction : undefined,
                client_id: headerClientId.length ? headerClientId.join(",") : undefined,
                network: headerNetwork || undefined,
                status: headerStatus || undefined,
            }));
        }
        catch (error) {
            console.error("Failed to create sync logs:", error);
        }
    }, [
        dispatch, errorLogPage, errorHeaderType, syncHeaderType,
        debouncedSearch, sortConfig.columnId, sortConfig.direction,
        headerClientId, headerNetwork, headerStatus,
    ]);
    // Delete (single or many) via one thunk, then refetch — clamp the page if it's now out of range, else refresh in place.
    const handleConfirmDelete = useCallback(async () => {
        const { type, ids } = deleteState;
        if (!type || ids.length === 0)
            return;
        try {
            setDeleteState((p) => ({ ...p, loading: true }));
            const action = type === "error" ? deleteErrorLog : deleteSyncLog;
            await dispatch(action(ids)).unwrap();
            const isErrorTab = type === "error";
            if (isErrorTab)
                setSelectedErrorLogIds(new Set());
            else
                setSelectedSyncLogIds(new Set());
            const oldTotal = isErrorTab ? errorLogPagination.total : syncLogPagination.total;
            const newTotal = Math.max(0, oldTotal - ids.length);
            const newLastPage = Math.max(1, Math.ceil(newTotal / SYNC_LOG_PAGINATION.LIMIT));
            const currentPage = isErrorTab ? errorLogPage : syncLogPage;
            const targetPage = Math.min(currentPage, newLastPage);
            if (targetPage !== currentPage) {
                // page moved → the deps-driven effect will refetch
                if (isErrorTab)
                    setErrorLogPage(targetPage);
                else
                    setSyncLogPage(targetPage);
            }
            else {
                // same page → manually refresh so the next batch of rows slides in
                dispatch(fetchSyncLogs({
                    error_page: errorLogPage,
                    error_limit: SYNC_LOG_PAGINATION.LIMIT,
                    error_request_type: errorHeaderType || undefined,
                    sync_page: syncLogPage,
                    sync_limit: SYNC_LOG_PAGINATION.LIMIT,
                    sync_type: syncHeaderType || undefined,
                    search: debouncedSearch || undefined,
                    sort_by: sortConfig.columnId || undefined,
                    sort_order: sortConfig.columnId ? sortConfig.direction : undefined,
                    client_id: headerClientId.length ? headerClientId.join(",") : undefined,
                    network: headerNetwork || undefined,
                    status: headerStatus || undefined,
                }));
            }
            setDeleteState({ open: false, loading: false, type: null, ids: [] });
        }
        catch (error) {
            console.error("Failed to delete log:", error);
            setDeleteState((p) => ({ ...p, loading: false }));
        }
    }, [
        deleteState, dispatch,
        errorLogPagination.total, syncLogPagination.total,
        errorLogPage, syncLogPage,
        errorHeaderType, syncHeaderType,
        debouncedSearch, sortConfig.columnId, sortConfig.direction,
        headerClientId, headerNetwork, headerStatus,
    ]);
    const isErrorTab = activeTab === "error_logs";
    const currentPagination = isErrorTab ? errorLogPagination : syncLogPagination;
    const currentPage = isErrorTab ? errorLogPage : syncLogPage;
    const setCurrentPage = isErrorTab ? setErrorLogPage : setSyncLogPage;
    return (_jsxs(PageContainer, { children: [_jsx(Box, { className: "process-log-container", children: _jsxs(Paper, { className: "process-log-card", elevation: 0, children: [_jsx(SyncLogHeader, { activeTab: activeTab, onTabChange: (tab) => {
                                setActiveTab(tab);
                            }, errorLogCount: errorLogPagination.total, syncLogCount: syncLogPagination.total, searchText: searchText, setSearchText: setSearchText, headerStatus: headerStatus, setHeaderStatus: setHeaderStatus, headerType: activeHeaderType, setHeaderType: setActiveHeaderType, headerNetwork: headerNetwork, setHeaderNetwork: setHeaderNetwork, headerClientId: headerClientId, setHeaderClientId: setHeaderClientId, typeOptionsList: activeTab === "sync_logs" ? syncLogTypes : errorLogTypes, clients: syncLogClients, onColumnVisibilityClick: (e) => setColumnVisibilityAnchor(e.currentTarget), onCreateSyncLog: handleOpenCreateSyncLog, onRefresh: handleRefresh, refreshing: errorLogLoading || syncLogLoading }), _jsx(Box, { className: "process-log-table-wrapper", children: isErrorTab ? (_jsx(ErrorLogList, { errorLogs: errorLogs, loading: errorLogLoading, isFirstLoad: isFirstLoad.current, searchText: searchText, onEdit: handleEditErrorLog, sortConfig: sortConfig, onSortChange: handleSortChange, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchor: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), columnOrder: columnOrder, onColumnOrderChange: setColumnOrder, pinnedColumns: pinnedColumns, onColumnPinningChange: setPinnedColumns, selectedRows: selectedErrorLogIds, onSelectionChange: setSelectedErrorLogIds })) : (_jsx(SyncLogList, { syncLogs: syncLogs, loading: syncLogLoading, isFirstLoad: isFirstLoad.current, searchText: searchText, onEdit: handleEditSyncLog, onView: handleViewSyncLog, sortConfig: sortConfig, onSortChange: handleSortChange, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchor: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), columnOrder: columnOrder, onColumnOrderChange: setColumnOrder, pinnedColumns: pinnedColumns, onColumnPinningChange: setPinnedColumns, selectedRows: selectedSyncLogIds, onSelectionChange: setSelectedSyncLogIds })) }), currentPagination.total > SYNC_LOG_PAGINATION.LIMIT && (_jsx(Box, { className: "process-log-pagination-wrapper", children: _jsx(Pagination, { currentPage: currentPage, totalPages: currentPagination.total_pages, onPageChange: setCurrentPage, maxVisiblePages: 5, totalEntries: currentPagination.total, itemsPerPage: SYNC_LOG_PAGINATION.LIMIT, showEntriesMessage: true }) }))] }) }), _jsx(EditErrorLogDrawer, { open: editErrorLogDrawerOpen, onClose: handleErrorLogDrawerClose, log: selectedErrorLog }), _jsx(AddEditSyncLogDrawer, { open: editSyncLogDrawerOpen, onClose: handleSyncLogDrawerClose, log: selectedSyncLog, saving: saving, onSave: handleSaveSyncLog, viewOnly: syncLogViewOnly }), _jsx(AddEditSyncLogDrawer, { open: createSyncLogDrawerOpen, onClose: handleCreateSyncLogClose, log: null, saving: saving, onSave: handleSaveSyncLog, onCreate: handleCreateSyncLog }), deleteState.open && (_jsx(ConfirmActionModal, { className: "delete-popup-container", open: deleteState.open, loading: deleteState.loading, title: "Are You Sure?", description: `${deleteState.ids.length} ${deleteState.type === "error" ? "error" : "sync"} log${deleteState.ids.length === 1 ? "" : "s"} will be permanently deleted. Once deleted, cannot be recovered.`, confirmText: "Yes, Proceed", cancelText: "No, Cancel", onClose: () => !deleteState.loading &&
                    setDeleteState({ open: false, loading: false, type: null, ids: [] }), onConfirm: handleConfirmDelete })), _jsx(FloatingSaveBar, { count: isErrorTab ? selectedErrorLogIds.size : selectedSyncLogIds.size, isVisible: (isErrorTab ? selectedErrorLogIds.size : selectedSyncLogIds.size) > 0, onReset: () => isErrorTab ? setSelectedErrorLogIds(new Set()) : setSelectedSyncLogIds(new Set()), message: `Selected ${isErrorTab ? "error" : "sync"} log${(isErrorTab ? selectedErrorLogIds.size : selectedSyncLogIds.size) === 1 ? "" : "s"}`, countLabel: "Selected", children: _jsx(Button, { className: "processlog-bulk-delete-button", onClick: isErrorTab ? handleBulkDeleteErrorLogs : handleBulkDeleteSyncLogs, disabled: deleteState.loading, startIcon: deleteState.loading ? _jsx(CircularProgress, { size: 16, color: "inherit" }) : _jsx(Trash2, { size: 18 }), children: "Delete" }) })] }));
};
export default SyncLogMain;
